import { datadogRum } from "@datadog/browser-rum";
import { getApplicationVersion, R, run } from "@specsheet-common/shared-tools";
import { N_UserProfile } from "@specsheet-common/shared-types";
import { getEnv, isLocalEnv } from "src/constants/util";
import axios from "axios";

let isSetup = false;

// Datadog tags allow lowercase letters, digits, and _ : . / - The full tag
// including the "version:" prefix must stay within 200 characters.
const toDatadogVersion = (version: string) =>
  version
    .toLowerCase()
    .replace(/[^\d./:_a-z-]/g, "-")
    .slice(0, 192);

export const setup = (recordAllSessions: boolean) => {
  if (isLocalEnv()) {
    return;
  }
  datadogRum.init({
    applicationId: "621aab26-8ab5-4cdf-b2a2-20a4cc02e720",
    clientToken: "pub0079f9b157e97aaa36837aa6ece2cd09",
    site: "datadoghq.com",
    service: "the-spec-sheet",
    env: getEnv(),
    version: toDatadogVersion(getApplicationVersion()),
    sessionSampleRate: 100,
    sessionReplaySampleRate: recordAllSessions ? 100 : 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    beforeSend: (event) => {
      if (event.type === "view") {
        event.view.url = document.location.href;
      }
      return true;
    },
  });

  datadogRum.startSessionReplayRecording();

  isSetup = true;
};

export const setUser = (profile: N_UserProfile) => {
  datadogRum.setUser({
    email: profile.email,
    id: profile.userId,
    name: profile.name + " " + profile.lastName,
  });
};

const EMPTY_ORGANISATION_ID = "noActiveOrg";
let lastAccountKey: string | null = null;

export const setAccount = (organisation: { id: string; name: string }) => {
  if (!organisation.id || organisation.id === EMPTY_ORGANISATION_ID) {
    if (lastAccountKey === null) {
      return;
    }
    lastAccountKey = null;
    datadogRum.clearAccount();
    return;
  }
  const accountKey = `${organisation.id}\0${organisation.name}`;
  if (accountKey === lastAccountKey) {
    return;
  }
  lastAccountKey = accountKey;
  datadogRum.setAccount({
    id: organisation.id,
    name: organisation.name,
  });
};

let lastCampaignViewKey: string | null = null;

export const setCampaignView = (campaign: {
  id: string;
  name: string;
  organisation: string;
  placementCount: number;
}) => {
  const campaignViewKey = [
    campaign.id,
    campaign.name,
    campaign.organisation,
    campaign.placementCount,
  ].join("\0");
  if (campaignViewKey === lastCampaignViewKey) {
    return;
  }
  lastCampaignViewKey = campaignViewKey;
  datadogRum.setViewContext({
    campaign: {
      id: campaign.id,
      name: campaign.name,
      organisation: campaign.organisation,
      placement_count: campaign.placementCount,
    },
  });
};

export const clearCampaignView = () => {
  if (lastCampaignViewKey === null) {
    return;
  }
  lastCampaignViewKey = null;
  datadogRum.setViewContext({});
};

/**
 * Every error encountered on page should be funneled through this method
 */
export const error = (e: unknown, context: object = {}) => {
  if (isSetup) {
    // eslint-disable-next-line no-console
    console.warn("error delegated to datadog", e, context);
    const message = R.isError(e) ? e.message : undefined;
    const data = run(() => {
      if (axios.isAxiosError(e)) {
        const axiosResponse = e.response;
        const axiosRequest = e.config;
        return {
          ...context,
          isAxiosError: true,
          axiosResponse,
          axiosRequest: R.mapValues({ ...axiosRequest?.headers }, (v, k) =>
            /authori.ation/i.test(String(k)) ? "" : v
          ),
          message,
        };
      } else {
        return { ...context, message };
      }
    });
    datadogRum.addError(e, data);
  } else {
    // eslint-disable-next-line no-console
    console.error("datadog not setup to handle error", e, context);
  }
};

/** Slow contract-variation placements lookup; used for Slack alerts with the email link. */
const CONTRACT_VARIATION_PLACEMENTS_SLOW_MS = 60_000;

/** Prefetch + table filter both mount the query hook; keep one RUM event per fetch. */
const trackedContractVariationFetches = new Set<string>();

export const trackContractVariationPlacementsRequest = ({
  campaignId,
  cmlId,
  extCampaignIds,
  durationMs,
  ok,
  dataUpdatedAt,
  errorUpdatedAt,
}: {
  campaignId: string;
  cmlId: string;
  extCampaignIds: string;
  durationMs: number;
  ok: boolean;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
}): void => {
  // Fast opens: no RUM work. Alert path only when the request is already slow.
  if (!isSetup || durationMs < CONTRACT_VARIATION_PLACEMENTS_SLOW_MS) {
    return;
  }
  const fetchKey = `${campaignId}|${cmlId}|${extCampaignIds}|${dataUpdatedAt}|${errorUpdatedAt}`;
  if (trackedContractVariationFetches.has(fetchKey)) {
    return;
  }
  trackedContractVariationFetches.add(fetchKey);
  if (trackedContractVariationFetches.size > 50) {
    trackedContractVariationFetches.clear();
    trackedContractVariationFetches.add(fetchKey);
  }
  datadogRum.addAction("contract_variation_placements_slow", {
    campaignId,
    cmlId,
    extCampaignIds,
    durationMs,
    ok,
    viewUrl: document.location.href,
  });
};
